import { useCallback, useEffect, useRef, useState } from 'react';
import { usePlinkoSound } from './usePlinkoSound';

export const usePlinkoDrops = (muted, volume, onLanded) => {
  const [rows, setRows] = useState(16), [risk, setRisk] = useState('medium');
  const [drops, setDrops] = useState([]), [last, setLast] = useState(null);
  const [recent, setRecent] = useState([]);
  const [hiddenHistoryIds, setHiddenHistoryIds] = useState(() => new Set());
  const active = useRef([]), seen = useRef(new Set());
  const sound = usePlinkoSound(muted, volume);
  const { play } = sound;
  // Reserve before the request: history polling can finish before the bet response.
  const reserve = useCallback((id) => setHiddenHistoryIds((current) => new Set([...current, id])), []);
  const release = useCallback((id) => {
    if (active.current.some((round) => round.request_id === id)) return;
    setHiddenHistoryIds((current) => {
      if (!current.has(id)) return current;
      const next = new Set(current); next.delete(id); return next;
    });
  }, []);
  const reveal = useCallback((round) => {
    release(round.request_id);
    setLast(round); setRecent((current) => [round, ...current.filter((item) => item.request_id !== round.request_id)].slice(0, 6));
    play('land', round.multiplier); onLanded?.(round);
  }, [play, onLanded, release]);
  useEffect(() => () => {
    // Completed server bets remain in the session when the page is left;
    // no animation is still visible after unmount. Preserve original owner.
    active.current.forEach((round) => onLanded?.(round));
  }, [onLanded]);
  const land = useCallback((round) => {
    if (!active.current.some((item) => item.request_id === round.request_id)) return;
    active.current = active.current.filter((item) => item.request_id !== round.request_id);
    setDrops(active.current); reveal(round);
  }, [reveal]);
  const add = useCallback((round, instant, turbo = false) => {
    if (seen.current.has(round.request_id)) return;
    seen.current.add(round.request_id);
    if (seen.current.size > 200) seen.current.delete(seen.current.values().next().value);
    play('drop');
    if (instant || window.matchMedia('(prefers-reduced-motion: reduce)').matches) { reveal(round); return; }
    active.current = [...active.current, { ...round, turbo }]; setDrops(active.current);
  }, [play, reveal]);
  const reset = useCallback(() => { active.current = []; seen.current.clear(); setDrops([]); setLast(null); setRecent([]); setHiddenHistoryIds(new Set()); }, []);
  return { rows, setRows, risk, setRisk, drops, last, recent, hiddenHistoryIds, reserve, release, add, land, reset, sound, active };
};